import { useCurrentSessionId } from '@heidi/runtime/shell';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import { applySessionSidePanel, voiceSessionPanelAtom } from '../ui/openSessionSidePanel';
import type { VoiceUiActionsPort } from '../ui/voice-ui-actions.port';

const PANEL_TTL_MS = 30_000;

/**
 * Applies a pending voice ``open_artifact`` tasks/codes panel once the
 * target session is current (cross-session navigates land here after mount).
 *
 * Takes `VoiceUiActionsPort` the way the appliers do, and for the same reason:
 * the applier it calls performs an app-shell write. The app binds it once, at
 * the composition host — never at the mount, which is what keeps the old path a
 * pure re-export shim.
 */
export const useApplyVoiceSessionPanel = (port: VoiceUiActionsPort) => {
  const sessionId = useCurrentSessionId();
  const pending = useAtomValue(voiceSessionPanelAtom);
  const setPending = useSetAtom(voiceSessionPanelAtom);

  useEffect(() => {
    if (!sessionId || !pending) return;
    if (pending.sessionId !== sessionId) return;
    if (Date.now() - pending.requestedAt > PANEL_TTL_MS) {
      setPending(null);
      return;
    }
    applySessionSidePanel(pending.sessionId, pending.panel, port);
    setPending(null);
  }, [sessionId, pending, setPending, port]);
};
